/**
 * The seller-side `EditorPort` implementation — wraps the EXISTING seller
 * hooks/API 1:1 so `ProductEditor` renders and behaves exactly as the
 * pre-extraction `ProductEditorPage.tsx` did. No new network calls, no new
 * query keys, no new cache invalidation — every hook call here is the same
 * hook, called the same way, that the monolithic page used to call
 * directly.
 */
import { useMemo } from "react";
import { useTranslation } from "react-i18next";

import {
  registerSellerDashboardI18n,
  SELLER_DASHBOARD_NS,
  type SellerProduct,
} from "@/features/seller-dashboard/api";
import {
  useCreateProduct,
  useCreateVariant,
  useDeleteVariant,
  usePatchProduct,
  useReorderPhotos,
  useSellerProduct,
  useSellerVariants,
} from "@/features/seller-dashboard/hooks/useSellerProducts";
import type {
  EditorCapabilities,
  EditorCreateBody,
  EditorDataBinding,
  EditorPort,
  EditorProduct,
  EditorWriteBody,
} from "@/features/product-editor/port";

function toEditorProduct(p: SellerProduct): EditorProduct {
  return {
    id: p.id,
    seller_id: p.seller_id,
    category_id: p.category_id,
    name: p.name,
    description: p.description,
    price_kgs: p.price_kgs,
    bulk_price_kgs: p.bulk_price_kgs,
    bulk_threshold: p.bulk_threshold,
    stock_state: p.stock_state,
    status: p.status,
    variant_group_id: p.variant_group_id,
    images: p.images,
    attributes: p.attributes,
    category_ids: p.category_ids,
    primary_category_id: p.primary_category_id,
    // v0048. Listed EXPLICITLY because this mapper names every field — a
    // property added to EditorProduct and not added here arrives as
    // `undefined`, the form renders it blank, and the next Save (which sends
    // the whole form) clears it in the database.
    unit: p.unit,
    brand: p.brand,
    article: p.article,
    formal_name: p.formal_name,
    tags: p.tags,
    folk_names: p.folk_names,
  };
}

export function useSellerEditorPort(): EditorPort {
  registerSellerDashboardI18n();
  const { t } = useTranslation(SELLER_DASHBOARD_NS);

  const capabilities: EditorCapabilities = {
    mode: "seller",
    showSellerPicker: false,
    statusControl: "submit-review",
    showSuggestTriggers: true,
    showVariants: true,
    forbiddenRedirectTo: "/account",
    productPathAfterCreate: (id) => `/sellers/me/products/${id}`,
    listPath: "/sellers/me/products",
    pageTitle: (isNew) => (isNew ? t("editor.newTitle") : t("editor.editTitle")),
  };

  function useData(productId: string | null): EditorDataBinding {
    const rawQuery = useSellerProduct(productId);
    const rawCreate = useCreateProduct();
    const rawPatch = usePatchProduct();
    const rawReorder = useReorderPhotos();

    // Each mutation object below is a THIN pass-through wrapper: same
    // `.isPending`/other status fields (from the spread), same underlying
    // `mutationFn`, only `.mutate`'s success callback re-maps the raw
    // `SellerProduct` result into an `EditorProduct` before handing it to
    // whatever `onSuccess` the caller (`ProductEditor.tsx`) supplied. The
    // `as EditorDataBinding["create"]` casts are safe: `ProductEditor.tsx`
    // (per Task F1) only ever reads `.mutate(...)` and `.isPending` off
    // these three objects — never `.data`/`.error`/`.mutateAsync`/etc — so
    // the cast doesn't paper over any behavior the component actually
    // depends on. `EditorCreateBody`/`EditorWriteBody` are structurally
    // identical to `SellerProductCreate`/`SellerProductUpdate` (same field
    // names/types; `EditorCreateBody` additionally carries an optional
    // `seller_id` that this adapter's `capabilities.showSellerPicker: false`
    // guarantees `ProductEditor.tsx` never actually sets — see Task F1's
    // `submitWith`), so passing the body straight through is safe.
    //
    // Memoized on `rawQuery.data` (TanStack Query keeps that reference
    // STABLE across re-renders until the query actually refetches/updates —
    // only `toEditorProduct`'s OWN wrapping needs stabilising). Without this,
    // `productQuery.data` is a brand-new object on every render regardless of
    // whether the underlying product changed, and `ProductEditor.tsx`'s
    // hydrate-on-load effect (keyed on `productQuery.data` by reference)
    // refires on literally every render — each firing calls `setPhotos(...)`
    // etc. with a fresh (referentially different) value, forcing another
    // render, forever (unbounded, accelerating re-render loop; React's own
    // "Maximum update depth exceeded" fires). Identical fix independently
    // verified in the admin adapter (`admin/products/editorPort.tsx`, Task
    // F3) — see that file's `useData` for the same pattern.
    const editorProduct = useMemo(
      () => (rawQuery.data ? toEditorProduct(rawQuery.data) : undefined),
      [rawQuery.data],
    );

    return {
      productQuery: {
        data: editorProduct,
        error: rawQuery.error,
      },
      create: {
        ...rawCreate,
        mutate: (body: EditorCreateBody, options) =>
          rawCreate.mutate(body, {
            // Installed @tanstack/react-query (5.100.11) `MutateOptions.onSuccess`
            // takes 4 params (`data, variables, onMutateResult, context`), not the
            // 3-param shape (`data, variables, context`) — forward all four.
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["create"],
      patch: {
        ...rawPatch,
        mutate: (vars: { id: string; body: EditorWriteBody }, options) =>
          rawPatch.mutate(vars, {
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["patch"],
      reorder: {
        ...rawReorder,
        mutate: (vars, options) =>
          rawReorder.mutate(vars, {
            onSuccess: (data, variables, onMutateResult, context) =>
              options?.onSuccess?.(
                toEditorProduct(data),
                variables,
                onMutateResult,
                context,
              ),
            onError: options?.onError,
          }),
      } as EditorDataBinding["reorder"],
    };
  }

  return {
    capabilities,
    useData,
    variants: {
      useList: useSellerVariants,
      useCreate: useCreateVariant,
      useDelete: useDeleteVariant,
    },
  };
}
